import React, {
  useMemo,
  useImperativeHandle,
  useRef,
  useCallback,
  useEffect,
} from "react";
import { useLocation } from "react-router-dom";
import "./index.scss";

// 常量抽离，只创建一次
const COLOR_LIST = [
  { progress: "#81A9E1", background: "#CDE0F3" },
  { progress: "#E790AD", background: "#F5D4D4" },
  { progress: "#B9D8E4", background: "#B9D8E4" },
  { progress: "#697FE0", background: "#BBCBE8" },
  { progress: "rgb(0, 165, 0)", background: "rgba(0, 165, 0, 0.3)" },
  { progress: "#8CA24D", background: "rgba(140, 162, 77, 0.3)" },
  { progress: "#69D4E0", background: "#BBE3E8" },
];

// 单词间隔时间(ms)
const WORD_INTERVAL = 300;

const Word = React.forwardRef((props, ref) => {
  const { text, wordVisable, wordcb } = props;
  const location = useLocation();
  
  // 容器ref，用于重置动画
  const containerRef = useRef(null);
  // 回调ref，避免闭包陷阱
  const wordcbRef = useRef(wordcb);
  wordcbRef.current = wordcb;
  // 标记组件是否已挂载
  const isMountedRef = useRef(false);
  // 备用定时器（防止 animationend 不触发）
  const fallbackTimerRef = useRef(null);

  // 缓存路由参数
  const { type, index } = useMemo(() => {
  const searchParams = new URLSearchParams(location.search);
    return {
      type: Number(searchParams.get("type") || 0),
      index: Number(searchParams.get("index") || 0),
    };
  }, [location.search]);

  // 缓存背景色索引
  const colorIndex = useMemo(() => {
    if (type === 0 && [0, 1].includes(index)) return 0;
    if (type === 0 && index === 2) return 1;
    if (type === 0 && index === 3) return 2;
    if (type === 1) return 3;
    if (type === 2 && [0, 1, 2].includes(index)) return 4;
    if (type === 2 && index === 3) return 5;
    return 0;
  }, [type, index]);

  // 缓存单词列表 - 只在text变化时重新计算
  const words = useMemo(() => {
    return text?.trim() ? text.trim().split(/\s+/) : [];
  }, [text]);

  // 总动画时长
  const totalDuration = words.length * WORD_INTERVAL;

  // 清理备用定时器
  const clearFallbackTimer = useCallback(() => {
    if (fallbackTimerRef.current) {
      clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
  }, []);

  // 启动备用定时器（防止 animationend 不触发导致卡死）
  const startFallbackTimer = useCallback((duration) => {
    clearFallbackTimer();
    fallbackTimerRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        wordcbRef.current?.();
      }
    }, duration + 100); // 额外100ms缓冲
  }, [clearFallbackTimer]);

  // 动画结束回调（使用事件委托，只绑定一次）
  const handleAnimationEnd = useCallback((e) => {
    // 只响应最后一个单词的动画结束事件
    if (e.target.dataset.last === "true") {
      clearFallbackTimer(); // 动画正常结束，清除备用定时器
      wordcbRef.current?.();
    }
  }, [clearFallbackTimer]);

  // ✅ 暴露给父组件的reload方法 - 纯CSS重置动画
  useImperativeHandle(ref, () => ({
    reload: () => {
      const container = containerRef.current;
      // 如果组件不可见，直接调用回调
      if (!container || !wordVisable || words.length === 0) {
        // 延迟调用，模拟动画完成
        setTimeout(() => {
          if (isMountedRef.current) {
            wordcbRef.current?.();
          }
        }, 100);
      return;
    }
      
      // 通过移除/添加class来重置CSS动画，无需React状态更新
      container.classList.remove("playing");
      // 强制浏览器重排，确保动画重置
      void container.offsetWidth;
      container.classList.add("playing");

      // 启动备用定时器
      startFallbackTimer(totalDuration);
    },
  }), [wordVisable, words.length, totalDuration, startFallbackTimer]);

  // 组件挂载/卸载处理
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      clearFallbackTimer();
    };
  }, [clearFallbackTimer]);

  // 无展示需求时，直接return null
  if (!wordVisable || words.length === 0) return null;

  return (
    <div
      ref={containerRef}
      className="detail-content-font word-container"
      style={{
        "--bg-color": COLOR_LIST[colorIndex].background,
        "--total-duration": `${totalDuration}ms`,
        "--word-count": words.length,
      }}
      onAnimationEnd={handleAnimationEnd}
    >
      {words.map((word, idx) => (
            <span
          key={idx}
          className="word-item"
          style={{ "--word-index": idx }}
          data-last={idx === words.length - 1 ? "true" : undefined}
            >
              {word}
            </span>
          ))}
    </div>
  );
});

export default Word;
